import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "kinews_cart_v1";
const UPLOADS_BASE_URL = "https://kinewsstudiolimited.co.uk";

function buildUploadedFileUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${UPLOADS_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore storage errors
    }
  }, [items]);

  const addItem = (product, opts = {}) => {
    const qty = Math.max(1, Number(opts.qty ?? product.qty ?? 1) || 1);

    const selectedOptions = Array.isArray(product.selected_options)
      ? product.selected_options.map((option) => ({
          id: option.id,
          group_name: option.group_name,
          label: option.label,
          price: Number(option.price || 0),
        }))
      : [];

    const uploadedFiles = Array.isArray(product.uploaded_files)
      ? product.uploaded_files.map((file, index) => {
          const filePath = file?.file_path || file?.temp_path || "";
          return {
            name: file?.name || `upload-${index + 1}`,
            preview: file?.preview || buildUploadedFileUrl(filePath),
            type: file?.type || "",
            file_path: filePath,
            temp_path: file?.temp_path || file?.file_path || "",
            uploaded: !!filePath,
          };
        })
      : [];

    const customText =
      typeof product.custom_text === "string" ? product.custom_text.trim() : "";

    const unitPrice = Number(product.final_price ?? product.price ?? 0);

    const key = [
      product.id,
      JSON.stringify(selectedOptions.map((option) => option.id || option.label)),
      customText,
      JSON.stringify(
        uploadedFiles.map((file) => file.file_path || file.preview || file.name),
      ),
    ].join("__");

    setItems((prev) => {
      const found = prev.find((x) => x.key === key);

      if (found) {
        return prev.map((x) =>
          x.key === key ? { ...x, qty: x.qty + qty } : x,
        );
      }

      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          image: product.image || "",
          qty,
          price: unitPrice,
          final_price: unitPrice,
          selected_options: selectedOptions,
          custom_text: customText,
          uploaded_files: uploadedFiles,
        },
      ];
    });
  };

  const removeItem = (key) =>
    setItems((prev) => prev.filter((x) => x.key !== key));

  const updateQty = (key, qty) => {
    const q = Math.max(1, Number(qty) || 1);
    setItems((prev) => prev.map((x) => (x.key === key ? { ...x, qty: q } : x)));
  };

  const clearCart = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce(
      (sum, x) =>
        sum + Number(x.final_price ?? x.price ?? 0) * Number(x.qty || 0),
      0,
    );
    const count = items.reduce((sum, x) => sum + Number(x.qty || 0), 0);
    return { subtotal, count };
  }, [items]);

  const [isOpen, setIsOpen] = useState(false);

  const value = useMemo(
    () => ({ items, addItem, removeItem, updateQty, clearCart, totals, isOpen, setIsOpen }),
    [items, totals, isOpen],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}